import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Search, MapPin, Star, Handshake, Check, Clock, Users, Building2, Gift, Copy, MessageCircle } from "lucide-react";

const Avatar = ({ src, name, size = 44 }) => (
  src ? <img src={src} alt={name} style={{ width: size, height: size }} className="rounded-xl object-cover border border-slate-200" />
      : <div style={{ width: size, height: size }} className="rounded-xl bg-slate-100 flex items-center justify-center text-slate-400"><Building2 size={size * 0.5} /></div>
);

export default function ProNetworkDirectory() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [hasBiz, setHasBiz] = useState(true);
  const [q, setQ] = useState("");
  const [city, setCity] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [recoText, setRecoText] = useState("");
  const [referral, setReferral] = useState(null);

  const load = () => {
    setLoading(true);
    const p = new URLSearchParams();
    if (q) p.set("q", q); if (city) p.set("city", city); if (categoryId) p.set("category_id", categoryId);
    api.get(`/pro/network/directory?${p.toString()}`).then((r) => {
      setItems(r.data.items || []); setTotal(r.data.total || 0); setHasBiz(r.data.has_business);
    }).finally(() => setLoading(false));
  };
  useEffect(() => { load(); api.get("/categories").then((r) => setCategories(r.data?.categories || r.data || [])).catch(() => {}); api.get("/pro/referral").then((r) => setReferral(r.data)).catch(() => {}); /* eslint-disable-next-line */ }, []);
  useEffect(() => { const t = setTimeout(load, 350); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q, city, categoryId]);

  const connect = async (biz) => {
    try { await api.post("/pro/network/connections", { to_business_id: biz.id, message: "" });
      toast.success(`Demande envoyée à ${biz.name}`); load(); setDetail((d) => d && d.id === biz.id ? { ...d, connection_status: "pending_out" } : d);
    } catch (e) { toast.error(e?.response?.data?.detail || "Impossible d'envoyer la demande"); }
  };
  const openDetail = (id) => api.get(`/pro/network/business/${id}`).then((r) => { setDetail(r.data); setRecoText(""); }).catch(() => toast.error("Profil indisponible"));
  const submitReco = async () => {
    if (recoText.trim().length < 5) { toast.error("Recommandation trop courte"); return; }
    try { await api.post("/pro/network/recommendations", { to_business_id: detail.id, text: recoText.trim() });
      toast.success("Recommandation publiée ⭐"); setRecoText(""); openDetail(detail.id);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  const StatusBtn = ({ b }) => {
    if (b.connection_status === "connected") return <Badge className="bg-emerald-100 text-emerald-800" data-testid={`net-connected-${b.id}`}><Check size={12} className="mr-1" />Connecté</Badge>;
    if (b.connection_status === "pending_out") return <Badge className="bg-amber-100 text-amber-800" data-testid={`net-pending-${b.id}`}><Clock size={12} className="mr-1" />En attente</Badge>;
    if (b.connection_status === "pending_in") return <Link to="/reseau/connexions" onClick={(e) => e.stopPropagation()}><Badge className="bg-sky-100 text-sky-800 cursor-pointer hover:bg-sky-200" data-testid={`net-respond-${b.id}`}>Vous a sollicité · Répondre</Badge></Link>;
    return <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); connect(b); }} disabled={!hasBiz} data-testid={`net-connect-${b.id}`}><Handshake size={14} className="mr-1" />Se connecter</Button>;
  };

  return (
    <div className="space-y-6" data-testid="pro-network-directory">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-extrabold text-slate-900">Réseau Pro local</h1>
        <p className="text-slate-500 text-sm mt-1">Trouvez des partenaires, échangez des recommandations et créez des collaborations entre entreprises d'Eure-et-Loir.</p>
      </div>

      {!hasBiz && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 text-sm rounded-xl p-3" data-testid="net-no-biz">
          Complétez d'abord votre <strong>mini-site</strong> pour apparaître dans l'annuaire et contacter d'autres pros.
        </div>
      )}

      {referral?.link && (
        <div className="rounded-2xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-white p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="net-referral">
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-slate-900 inline-flex items-center gap-2"><Gift size={16} className="text-emerald-600" />Invitez un confrère</div>
            <p className="text-xs text-slate-500 mt-0.5">Faites grandir le réseau pro du 28 — chaque professionnel qui rejoint renforce la visibilité locale de tous.</p>
          </div>
          <div className="flex gap-2 shrink-0">
            <Button variant="outline" size="sm" onClick={async () => { try { await navigator.clipboard.writeText(referral.link); toast.success("Lien d'invitation copié !"); } catch { toast.message("Copie manuelle du lien."); } }} data-testid="net-referral-copy"><Copy size={14} className="mr-1" />Copier le lien</Button>
            <a href={`https://wa.me/?text=${encodeURIComponent(`Rejoins le Réseau Pro local de PROXIreduc-28 (Eure-et-Loir), c'est gratuit : ${referral.link}`)}`} target="_blank" rel="noreferrer" data-testid="net-referral-wa"><Button size="sm" className="bg-[#25D366] hover:bg-[#1ebe5b] text-white"><MessageCircle size={14} className="mr-1" />WhatsApp</Button></a>
          </div>
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un pro (nom)…" className="pl-9" data-testid="net-search" />
        </div>
        <Input value={city} onChange={(e) => setCity(e.target.value)} placeholder="Ville" className="sm:w-48" data-testid="net-city" />
        <select value={categoryId} onChange={(e) => setCategoryId(e.target.value)} className="h-10 rounded-md border border-slate-200 px-3 text-sm sm:w-56" data-testid="net-category">
          <option value="">Tous les secteurs</option>
          {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </div>

      <div className="text-xs text-slate-400">{loading ? "Chargement…" : `${total} professionnel(s)`}</div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((b) => (
          <div key={b.id} onClick={() => openDetail(b.id)} className="bg-white border border-slate-200 rounded-2xl p-4 hover:shadow-md hover:border-[#E05638]/40 transition-all cursor-pointer" data-testid={`net-card-${b.id}`}>
            <div className="flex items-start gap-3">
              <Avatar src={b.logo} name={b.name} />
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-slate-900 truncate">{b.name}</div>
                <div className="text-xs text-slate-500 flex items-center gap-2 mt-0.5">
                  {b.city && <span className="inline-flex items-center gap-1"><MapPin size={11} />{b.city}</span>}
                  {b.category_name && <span className="truncate">· {b.category_name}</span>}
                </div>
                {b.reco_count > 0 && <div className="text-xs text-amber-600 mt-1 inline-flex items-center gap-1"><Star size={11} className="fill-amber-500 text-amber-500" />{b.reco_count} reco</div>}
              </div>
            </div>
            {b.headline && <p className="text-sm text-slate-600 mt-2 line-clamp-2">{b.headline}</p>}
            {(b.propose || []).length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">{b.propose.slice(0, 3).map((t, i) => <span key={i} className="text-[11px] bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-full">propose : {t}</span>)}</div>
            )}
            <div className="mt-3 flex justify-end"><StatusBtn b={b} /></div>
          </div>
        ))}
      </div>
      {!loading && items.length === 0 && <div className="text-center text-slate-400 py-10"><Users className="mx-auto mb-2" />Aucun professionnel trouvé.</div>}

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="net-detail-dialog">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-3">
                  <Avatar src={detail.logo} name={detail.name} size={48} />
                  <div><div>{detail.name}</div><div className="text-xs font-normal text-slate-500">{detail.city} · {detail.category_name}</div></div>
                </DialogTitle>
              </DialogHeader>
              {detail.headline && <p className="text-sm font-medium text-slate-700">{detail.headline}</p>}
              {detail.bio && <p className="text-sm text-slate-600 whitespace-pre-line">{detail.bio}</p>}
              {(detail.cherche || []).length > 0 && <div className="flex flex-wrap gap-1"><span className="text-xs text-slate-400 mr-1">Cherche :</span>{detail.cherche.map((t, i) => <span key={i} className="text-[11px] bg-sky-50 text-sky-700 px-2 py-0.5 rounded-full">{t}</span>)}</div>}
              {(detail.propose || []).length > 0 && <div className="flex flex-wrap gap-1"><span className="text-xs text-slate-400 mr-1">Propose :</span>{detail.propose.map((t, i) => <span key={i} className="text-[11px] bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-full">{t}</span>)}</div>}

              {detail.connection_status === "connected" && (detail.phone || detail.email) && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 text-sm space-y-1" data-testid="net-contact">
                  <div className="font-semibold text-emerald-800">Coordonnées (connexion acceptée)</div>
                  {detail.phone && <div>📞 {detail.phone}</div>}
                  {detail.email && <div>✉️ {detail.email}</div>}
                  {detail.website && <div>🌐 <a className="text-[#E05638] underline" href={detail.website} target="_blank" rel="noreferrer">{detail.website}</a></div>}
                </div>
              )}

              <div className="flex items-center gap-2">
                <StatusBtn b={detail} />
              </div>

              <div className="border-t border-slate-100 pt-3">
                <div className="font-semibold text-sm text-slate-800 mb-2 inline-flex items-center gap-1"><Star size={14} className="text-amber-500" />Recommandations ({(detail.recommendations || []).length})</div>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {(detail.recommendations || []).map((r, i) => (
                    <div key={i} className="text-sm bg-slate-50 rounded-lg p-2">
                      <div className="text-slate-700">“{r.text}”</div>
                      <div className="text-[11px] text-slate-400 mt-1">— {r.from.name}{r.from.city ? `, ${r.from.city}` : ""}</div>
                    </div>
                  ))}
                  {(detail.recommendations || []).length === 0 && <div className="text-xs text-slate-400">Aucune recommandation pour le moment.</div>}
                </div>
                {detail.can_recommend && (
                  <div className="mt-3 flex gap-2">
                    <Input value={recoText} onChange={(e) => setRecoText(e.target.value)} placeholder="Recommandez ce pro en une phrase…" data-testid="net-reco-input" />
                    <Button onClick={submitReco} className="bg-[#E05638] hover:bg-[#c94a2f]" data-testid="net-reco-submit">Publier</Button>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
